import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { SettingsService, SettingsSection } from '../settings/settings.service';
import { ConfigurationService } from '../services/configuration.service';
import { AIAssistantService } from '../services/ai-assistant.service';
import { EventPinService, PinnedEventState } from '../events/event-pin.service';
import { NotificationsService } from '../services/notifications.service';
import { SharedService } from '../shared.service';
import { EntitlementService } from '../shared/entitlement.service';
import { UpgradePromptService } from '../shared/upgrade-prompt/upgrade-prompt.service';
import { UiThemeControlComponent } from '../shared/ui/ui-theme-control.component';
import { environment } from '../../environment';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterModule, UiThemeControlComponent],
  templateUrl: './app-sidebar.component.html',
  styleUrl: './app-sidebar.component.css',
})
export class AppSidebarComponent implements OnInit, OnDestroy {
  @Input() open = false;
  @Input() collapsed = false;
  @Output() openChange = new EventEmitter<boolean>();
  @Output() collapsedChange = new EventEmitter<boolean>();
  @Output() navigate = new EventEmitter<void>();

  userDisplayName = '';
  userInitials = '';
  private accountName = '';
  profileLink = '';
  currentRoute = '';
  isAuthenticated = false;
  isAdmin = false;
  isSigningOut = false;
  settingsOpen = false;
  readonly showEventsFeature = false;
  settingsSections: SettingsSection[] = [];
  pinnedEvent: PinnedEventState = {
    pinned: false,
    event_id: null,
    event_name: null,
  };
  unreadNotifications = 0;
  notesOpen = false;
  notesError = '';
  notableEvents: { type: string; title: string; at: string }[] = [];
  readonly currentYear = new Date().getFullYear();
  isSubscriptionActive = false;
  hasPaidPro = false;
  subscriptionStatusKnown = false;
  entitlementPhase: 'unknown' | 'loading' | 'entitled' | 'not_entitled' | 'error' | 'unauthenticated' = 'unknown';

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private dataService: SharedService,
    private entitlement: EntitlementService,
    private authService: AuthService,
    private settingsService: SettingsService,
    private configService: ConfigurationService,
    private aiAssistantService: AIAssistantService,
    private eventPinService: EventPinService,
    private notificationsService: NotificationsService,
    private upgradePrompt: UpgradePromptService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentRoute = this.router.url;
    this.settingsOpen = this.isSettingsActive();

    this.eventPinService.pinned$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.pinnedEvent = state;
      this.cdr.markForCheck();
    });

    this.notificationsService.unreadCount$.pipe(takeUntil(this.destroy$)).subscribe((count) => {
      this.unreadNotifications = count;
      this.cdr.markForCheck();
    });

    this.authService.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
      if (isAuthenticated) {
        this.eventPinService.loadPinned().subscribe();
        this.loadNotes();
        this.entitlement.load();
      } else {
        this.eventPinService.clearLocal();
        this.notificationsService.clearLocal();
        this.entitlement.clear();
      }
    });

    this.authService.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.isAdmin = user?.isAdmin || false;
      if (user) {
        this.accountName = this.joinedName(user.firstname, user.lastname);
        this.updateDisplayName(user.firstname, user.lastname, user.email);
        this.eventPinService.loadPinned().subscribe();
        this.loadNotes();
        this.entitlement.load();
      }
    });

    this.entitlement.snapshot$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.entitlementPhase = state.phase;
      this.subscriptionStatusKnown = state.phase === 'entitled' || state.phase === 'not_entitled';
      this.isSubscriptionActive = state.phase === 'entitled';
      this.hasPaidPro = state.phase === 'entitled' && state.paid;
      this.cdr.markForCheck();
    });

    this.settingsService.getSettingsSections().pipe(takeUntil(this.destroy$)).subscribe((sections) => {
      this.settingsSections = sections;
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.currentRoute = this.router.url;
        // Keep the Settings submenu open while browsing settings sections.
        this.settingsOpen = this.isSettingsActive();
        this.cdr.markForCheck();
      });

    this.dataService.currentData.pipe(takeUntil(this.destroy$)).subscribe((json) => {
      try {
        const parsed = JSON.parse(json || '{}');
        if (!this.accountName) {
          this.updateDisplayName(parsed.firstname, parsed.lastname, parsed.email || parsed.userEmail);
        }
        this.profileLink = parsed.profileLink || '';
      } catch {
        // Invalid shared data
      }
    });
  }

  get planLine(): { text: string; upgrade: boolean } | null {
    const rows = Array.isArray(this.entitlement.snapshot.subscriptions) ? this.entitlement.snapshot.subscriptions : [];
    const now = Date.now();
    let paid = false;
    let trialEnd = 0;
    let cardEnd = 0;
    for (const row of rows) {
      const status = String(row?.status || '').toLowerCase();
      const planId = String(row?.plan_id || '').toLowerCase();
      const stripeId = String(row?.stripe_subscription_id || '');
      const end = this.parseEnd(row?.current_period_end);
      const inForce = end === null || end > now;
      if (!inForce) continue;
      if (planId.includes('card') && end) {
        cardEnd = Math.max(cardEnd, end);
        continue;
      }
      const trialPlan = planId === 'trial_pro_30' || planId.includes('trial');
      if ((status === 'active' || status === 'canceled') && !trialPlan && (stripeId.startsWith('sub_') || (status === 'active' && stripeId !== ''))) {
        paid = true;
      }
      if (status === 'trialing' && end) {
        trialEnd = Math.max(trialEnd, end);
      }
    }
    if (paid) {
      return { text: 'PRO', upgrade: false };
    }
    if (cardEnd > now) {
      return { text: `Includes PRO until ${this.formatDay(cardEnd)}`, upgrade: false };
    }
    if (trialEnd > now) {
      const days = Math.max(0, Math.ceil((trialEnd - now) / 86400000));
      return { text: `PRO trial · ${days} ${days === 1 ? 'day' : 'days'} left`, upgrade: true };
    }
    if (this.entitlement.snapshot.phase === 'not_entitled') {
      return { text: 'Basic plan', upgrade: true };
    }
    return null;
  }

  get showUpgradeCta(): boolean {
    if (!this.isAuthenticated) {
      return false;
    }
    if (this.entitlementPhase === 'not_entitled') {
      return true;
    }
    return this.entitlementPhase === 'entitled' && !this.hasPaidPro;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get companyWebsiteUrl(): string {
    return this.readConfig('companyWebsiteUrl', 'https://innovnet.ai/');
  }

  get infoEmail(): string {
    return this.readConfig('infoEmail', 'inquiry@innovnet.ai');
  }

  get isNonProduction(): boolean {
    return !this.isProduction();
  }

  getActiveSections(): SettingsSection[] {
    return this.settingsSections.filter((section) => this.isSectionVisible(section));
  }

  getInactiveSections(): SettingsSection[] {
    return this.settingsSections.filter((section) => !section.active);
  }

  isContactsSectionActive(): boolean {
    const url = this.currentRoute?.split('?')[0] ?? '';
    return url === '/exchangedContacts' || url.startsWith('/contact-detail/');
  }

  isCardModeActive(): boolean {
    return (this.currentRoute?.split('?')[0] ?? '') === '/card-mode';
  }

  openCardMode(event: Event): void {
    event.preventDefault();
    if (this.entitlement.accessDenied) {
      this.upgradePrompt.show(
        'Card Mode is a PRO feature',
        'Choose what happens when someone taps your NFC card — Custom URL, Lead Capture, or Landing Page. Upgrade to Pro to unlock Card Mode.'
      );
      return;
    }
    void this.router.navigate(['/card-mode']);
    this.onNav();
  }

  isSettingsActive(): boolean {
    return (this.currentRoute?.split('?')[0] ?? '').startsWith('/settings');
  }

  isSettingsSectionActive(sectionId: string): boolean {
    const hash = (this.currentRoute || '').split('#')[1] || '';
    if (!hash) {
      return sectionId === 'card-information';
    }
    return hash === sectionId;
  }

  toggleSettings(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.settingsOpen = !this.settingsOpen;
  }

  closeSettings(): void {
    this.settingsOpen = false;
  }

  onNav(): void {
    this.settingsOpen = false;
    this.navigate.emit();
  }

  /** Stay inside Settings — keep the submenu open, only close the mobile drawer. */
  onSettingsNav(): void {
    this.settingsOpen = true;
    this.navigate.emit();
  }

  openAIAssistant(event: Event): void {
    event.preventDefault();
    this.aiAssistantService.open();
    this.onNav();
  }

  goToPinnedEvent(): void {
    if (this.pinnedEvent.pinned && this.pinnedEvent.event_id) {
      void this.router.navigate(['/events', this.pinnedEvent.event_id]);
      this.onNav();
    }
  }

  goToContactsFromNotifications(): void {
    this.notificationsService.markAllRead().subscribe();
    void this.router.navigate(['/exchangedContacts']);
    this.onNav();
  }

  goUpgrade(): void {
    void this.router.navigate(['/settings'], {
      fragment: 'billing',
      queryParams: { section: 'billing' },
    });
    this.onNav();
  }

  comingSoon(event: Event): void {
    event.preventDefault();
  }

  signOut(): void {
    if (this.isSigningOut) {
      return;
    }
    this.isSigningOut = true;
    this.authService.clearAuthStateImmediately();
    this.notificationsService.clearLocal();
    this.authService.logout().subscribe({
      next: () => {
        this.isSigningOut = false;
      },
      error: () => {
        this.isSigningOut = false;
        this.router.navigate(['/signin']);
      },
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.settingsOpen) {
      this.settingsOpen = false;
    }
  }

  private joinedName(firstname?: string, lastname?: string): string {
    return [firstname, lastname].filter((part) => !!part && part.trim().length > 0).join(' ').trim();
  }

  toggleNotifications(event: Event): void {
    event.stopPropagation();
    this.notesOpen = !this.notesOpen;
    if (this.notesOpen) this.loadNotes();
  }

  loadNotes(): void {
    this.notesError = '';
    this.notificationsService.notable().subscribe({
      next: (feed) => {
        this.notableEvents = feed.events;
        this.unreadNotifications = feed.unread;
        this.cdr.markForCheck();
      },
      error: () => {
        this.notesError = "Couldn't load.";
        this.cdr.markForCheck();
      },
    });
  }

  markNotesRead(): void {
    this.notificationsService.markAllRead().subscribe(() => this.loadNotes());
  }

  private parseEnd(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') return null;
    const text = String(raw).trim();
    const parsed = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text) ? Date.parse(text.replace(' ', 'T')) : Date.parse(text);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private formatDay(ms: number): string {
    return new Date(ms).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  }

  private updateDisplayName(firstname?: string, lastname?: string, fallbackEmail?: string): void {
    const nameParts = [firstname, lastname].filter((part) => !!part && part.trim().length > 0);
    if (nameParts.length > 0) {
      this.userDisplayName = nameParts.join(' ').trim();
      this.userInitials = nameParts
        .slice(0, 2)
        .map((part) => (part || '').trim().charAt(0).toUpperCase())
        .join('');
      return;
    }
    if (fallbackEmail) {
      this.userDisplayName = fallbackEmail.split('@')[0] || '';
      this.userInitials = this.userDisplayName.slice(0, 2).toUpperCase();
      return;
    }
    this.userDisplayName = '';
    this.userInitials = '';
  }

  private isSectionVisible(section: SettingsSection): boolean {
    if (this.isProduction() && section.id === 'card-information') {
      return false;
    }
    if (section.id === 'qr-payment') {
      return this.isAdmin;
    }
    if (this.isProduction() && !section.active) {
      return false;
    }
    return true;
  }

  private isProduction(): boolean {
    try {
      return this.configService.isProductionEnvironment();
    } catch {
      return !!environment.production;
    }
  }

  private readConfig(key: 'companyWebsiteUrl' | 'infoEmail', fallback: string): string {
    try {
      const value = this.configService.getConfig()[key];
      return (value || fallback).toString();
    } catch {
      return fallback;
    }
  }
}
